/* eslint-disable max-len */
import { useNavigate } from 'react-router-dom';

const serviceCards = [
  {
    icon: "ri-brain-line",
    title: "NLP-Informed Coaching",
    description:
      "Targeted mindset shifts to break the mental patterns that keep you stuck in the boom and bust cycle.",
  },
  {
    icon: "ri-mental-health-line",
    title: "Nervous System Regulation",
    description:
      "Practical tools to move your body out of fight or flight and into a state of safety and clarity.",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Individualised Strategy",
    description:
      "Realistic, energy-safe plans tailored to your specific diagnosis, lifestyle, and current capacity.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Energy Pattern Exploration",
    description:
      "A gentle exploration of how you use your energy, helping you find your sustainable baseline.",
  },
  {
    icon: "ri-moon-line",
    title: "Hypnotic Relaxation",
    description:
      "Optional, deep-rest practices designed to reset your nervous system at a cellular level.",
  },
];

export default function ServicesSection() {
  const navigate = useNavigate();

  return (
    <section id="services" className="section-padding scroll-mt-24 bg-background-100">
      <div className="max-w-6xl mx-auto">
        {/* Course Promo Banner */}
        <div className="mb-14 max-w-4xl mx-auto">
          <div className="bg-[#6B8FA3] rounded-xl p-7 md:p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase bg-white/15 text-white rounded-full mb-3 border border-white/20">
                  New Course
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-bold mb-2">
                  The Energy Safe Journey
                </h3>
                <p className="text-sm text-white/80 leading-relaxed max-w-lg">
                  A self-paced course with 5 structured Energy Paths + downloadable tools to protect your baseline, eliminate crashes, and reclaim your day.
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <span className="text-lg text-white/50 line-through">£197</span>
                  <span className="text-2xl font-bold text-white">£97</span>
                  <span className="text-xs text-white/60">Launch price</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/course')}
                  className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-6 py-3 rounded-lg font-medium text-sm hover:bg-white/10 hover:scale-[1.03] hover:-translate-y-[3px] transition-all duration-300 ease-out whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-sm" />
                  </span>
                  Learn More
                </button>
                <a
                  href="https://federica-tedeschi3.systeme.io/course"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#2C3E6B] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#24325A] hover:scale-[1.03] hover:-translate-y-[3px] hover:shadow-lg transition-all duration-300 ease-out whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shopping-cart-line text-sm" />
                  </span>
                  Enrol Now
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-800 mb-4">
            How I Support You
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-base leading-relaxed italic">
            Transforming the way you live, from the inside out.
          </p>
        </div>

        {/* Intro */}
        <div className="max-w-4xl mx-auto mb-10 text-sm text-foreground-600 leading-relaxed">
          <p>
            The <strong>Energy-Safe Method</strong> moves beyond pushing through. Instead, we update the internal systems that run your life, working with your subconscious and nervous system to shift the deep-seated patterns that keep you stuck in exhaustion.
          </p>
        </div>

        {/* Who This Supports */}
        <div className="mb-10 max-w-4xl mx-auto space-y-4 text-sm text-foreground-600 leading-relaxed">
          <h3 className="font-heading text-xl font-bold text-primary-800">Who This Supports:</h3>
          <p className="font-medium text-primary-700 text-sm">
            My Energy-Safe Method and Energy-Safe Course are designed specifically for three groups:
          </p>
          <ul className="space-y-2 pl-1">
            <li className="flex items-start gap-2">
              <span className="mt-1 w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-line text-accent-600" />
              </span>
              <span>
                <strong className="text-primary-700">Chronic Illness:</strong> fatigue, pain and unpredictable physical symptoms — including the &ldquo;Boom &amp; Bust&rdquo; cycle, burnout, trauma recovery and nervous system dysregulation.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-line text-accent-600" />
              </span>
              <span>
                <strong className="text-primary-700">ADHD &amp; Autism:</strong> sensory overload, focus leakage and emotional drain, plus the masking and executive load that quietly uses up your day.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-line text-accent-600" />
              </span>
              <span>
                <strong className="text-primary-700">Life-Demand Mismatch:</strong> when daily responsibilities outpace your energy — new diagnoses, caring roles or seasons where life asks more than your body can give.
              </span>
            </li>
          </ul>
          <p className="text-sm text-foreground-600">
            Together, we use <strong className="text-primary-700">Energy-Safe Motions</strong> to break the Boom &amp; Bust cycle and build sustainable, guilt-free progress.
          </p>
        </div>

        {/* Why It Works */}
        <div className="mb-12 max-w-4xl mx-auto space-y-4 text-sm text-foreground-600 leading-relaxed">
          <h3 className="font-heading text-xl font-bold text-primary-800">Why It Works:</h3>
          <p>
            Most of our habits and stress responses are managed by the subconscious. Instead of fighting your symptoms with willpower, we:
          </p>
          <ol className="space-y-3 pl-1">
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-xs font-bold text-background-50">
                1
              </span>
              <span>
                <strong className="text-primary-700">Create Safety:</strong> We calm the fight or flight response to stop the crash before it starts.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-xs font-bold text-background-50">
                2
              </span>
              <span>
                <strong className="text-primary-700">Update Patterns:</strong> Using NLP, we gently rewire the subconscious habits that lead to overwhelm.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-xs font-bold text-background-50">
                3
              </span>
              <span>
                <strong className="text-primary-700">Build Resilience:</strong> We restore self-trust and create a sustainable balance in how you feel and function.
              </span>
            </li>
          </ol>
        </div>

        {/* NLP Toolset */}
        <div className="mb-14 max-w-4xl mx-auto rounded-xl p-7 border border-accent-300/40 bg-accent-50/30">
          <h3 className="font-heading text-xl font-bold text-primary-800 mb-3">
            The NLP Toolset: Rewiring the Mind-Body Connection
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed mb-5">
            NLP (Neuro-Linguistic Programming) is the software update for your nervous system. It helps you change the internal dialogue that drives your physical and emotional responses.
          </p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-2 text-sm text-foreground-600">
              <span className="mt-1 w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-line text-accent-600" />
              </span>
              <span>
                <strong className="text-primary-700">Break the Loop:</strong> Identify and dismantle the limiting beliefs like &ldquo;my worth is my productivity&rdquo;, that trigger stress and crashes.
              </span>
            </li>
            <li className="flex items-start gap-2 text-sm text-foreground-600">
              <span className="mt-1 w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-line text-accent-600" />
              </span>
              <span>
                <strong className="text-primary-700">Rewire the Response:</strong> Shift your subconscious habits so your body defaults to safety and calm rather than &ldquo;fight or flight.&rdquo;
              </span>
            </li>
            <li className="flex items-start gap-2 text-sm text-foreground-600">
              <span className="mt-1 w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-line text-accent-600" />
              </span>
              <span>
                <strong className="text-primary-700">Sustainable Change:</strong> Move beyond willpower. We update the mental patterns that make healthy boundaries and &ldquo;strategic reloading&rdquo; feel natural, not forced.
              </span>
            </li>
          </ul>
          <a
            href="https://calendly.com/federicatedeschi3/new-meeting"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-primary-500 text-background-50 px-7 py-3 rounded-lg font-medium text-sm hover:bg-primary-600 hover:scale-[1.05] hover:-translate-y-[3px] hover:shadow-lg transition-all duration-300 ease-out whitespace-nowrap cursor-pointer"
          >
            Book a Free Conversation
          </a>
        </div>

        {/* Bottom Grid */}
        <div className="grid md:grid-cols-2 gap-14 items-start">
          {/* Left Text */}
          <div className="space-y-5 text-foreground-600 text-sm leading-relaxed">
            <h4 className="font-heading text-lg font-bold text-primary-800">
              Stop waiting for better to start living.
            </h4>
            <p>
              Are you stuck waiting for the pain to ease before you allow yourself to truly live? The Boom and Bust cycle is an exhausting trap: you push through, you crash, you feel the guilt of resting, and then you repeat.
            </p>
            <p>
              Your mind and body are inseparably linked. When your body is depleted, your nervous system retreats into a survival state, making pain feel louder and energy feel lower. But you don&apos;t have to outrun your symptoms to find peace.
            </p>
            <div className="rounded-lg px-5 py-4 border-l-4 border-accent-400 bg-accent-50/30">
              <p className="text-sm font-semibold text-primary-700 mb-1">
                The <strong>Energy-Safe Shift</strong>:
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Create a lasting partnership with your body. By shifting the patterns that drain your capacity and amplify pain, we move your physiology out of survival mode and into a genuine state of repair and safety.
              </p>
            </div>
          </div>

          {/* Right - Service Cards */}
          <div className="flex flex-col gap-5">
            {serviceCards.map((card) => (
              <div
                key={card.title}
                className="bg-background-50 rounded-xl p-6 hover:shadow-card hover:scale-[1.03] hover:-translate-y-[3px] transition-all duration-300 ease-out group cursor-default flex items-start gap-4"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-200/40 shrink-0 group-hover:bg-accent-300/50 transition-colors">
                  <i className={`${card.icon} text-lg text-accent-700`} />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary-800 mb-1">
                    {card.title}
                  </h3>
                  <p className="text-foreground-600 leading-relaxed text-sm">
                    {card.description}
                  </p>
                </div>
              </div>
            ))}
            <div className="pt-2">
              <a
                href="https://calendly.com/federicatedeschi3/new-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary-500 text-background-50 px-7 py-3 rounded-lg font-medium text-sm hover:bg-primary-600 hover:scale-[1.05] hover:-translate-y-[3px] hover:shadow-lg transition-all duration-300 ease-out whitespace-nowrap cursor-pointer"
              >
                Book a Free Conversation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}